import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Shared factory for the offline queues (lesson completions, flashcard
 * reviews/sessions, practice sessions). Each queue is a zustand store
 * persisted to localStorage under its own key, holding one or more named
 * collections of pending entries.
 *
 * The three consumers used to be near-verbatim copies of each other
 * (progress/pendingQueueStore, flashcards/pendingReviewStore,
 * practice/pendingPracticeStore — since deleted) and had already started to diverge —
 * this factory is the single owner of the queue semantics now. Flusher
 * policy (retry cadence, backoff, retention, grace) lives next door in
 * `useQueueFlusher.ts`.
 */

export interface PendingQueueEntry {
  userId: string;
  queuedAt: number;
  /** Incremented on every failed flush attempt. */
  attempts?: number;
  /** Set when the flusher passes `at` to markAttempted (backoff gate). */
  lastAttemptAt?: number;
}

type CollectionShapes = Record<string, PendingQueueEntry>;

export type PendingQueueState<S extends CollectionShapes> = {
  [K in keyof S]: S[K][];
} & {
  enqueue: <K extends keyof S>(collection: K, items: S[K][]) => void;
  remove: <K extends keyof S>(collection: K, keys: readonly string[]) => void;
  /**
   * Bump attempts for the given entries. Pass `at` to also stamp
   * `lastAttemptAt` (required for backoff-gated flushers); omit it to
   * keep the legacy attempts-only shape (lesson queue).
   */
  markAttempted: <K extends keyof S>(collection: K, keys: readonly string[], at?: number) => void;
  /** Drop entries older than the retention window (junk guard). */
  pruneOlderThan: (cutoff: number) => void;
};

/**
 * Create a persisted offline-queue store.
 *
 * `collections` maps each collection name to its key extractor — the
 * collection names become the persisted state keys, so they must match
 * the shape already in users' localStorage for an existing storageKey.
 */
export function createPendingQueueStore<S extends CollectionShapes>(opts: {
  storageKey: string;
  collections: { [K in keyof S]: (item: S[K]) => string };
}) {
  type State = PendingQueueState<S>;
  const names = Object.keys(opts.collections) as (keyof S)[];

  // Internal implementation works on the widened entry type — TS can't relate
  // `S[K]` across the store's mapped state when K is generic, and the public
  // signatures on PendingQueueState already enforce per-collection item types
  // at every call site.
  const keyFns = opts.collections as unknown as Record<
    keyof S,
    (item: PendingQueueEntry) => string
  >;

  const useStore = create<State>()(
    persist(
      (set) => {
        const listOf = (s: State, k: keyof S): PendingQueueEntry[] =>
          (s as unknown as Record<keyof S, PendingQueueEntry[]>)[k];
        const patch = (k: keyof S, items: PendingQueueEntry[]): Partial<State> =>
          ({ [k]: items }) as unknown as Partial<State>;

        const emptyCollections = Object.fromEntries(
          names.map((n) => [n, []]),
        ) as unknown as { [K in keyof S]: S[K][] };

        return {
          ...emptyCollections,
          enqueue: <K extends keyof S>(collection: K, items: S[K][]) =>
            set((s) => patch(collection, [...listOf(s, collection), ...items])),
          remove: <K extends keyof S>(collection: K, keys: readonly string[]) =>
            set((s) => {
              const keyOf = keyFns[collection];
              return patch(
                collection,
                listOf(s, collection).filter((e) => !keys.includes(keyOf(e))),
              );
            }),
          markAttempted: <K extends keyof S>(
            collection: K,
            keys: readonly string[],
            at?: number,
          ) =>
            set((s) => {
              const keyOf = keyFns[collection];
              return patch(
                collection,
                listOf(s, collection).map((e) =>
                  keys.includes(keyOf(e))
                    ? {
                        ...e,
                        attempts: (e.attempts ?? 0) + 1,
                        ...(at !== undefined ? { lastAttemptAt: at } : {}),
                      }
                    : e,
                ),
              );
            }),
          pruneOlderThan: (cutoff: number) =>
            set((s) => {
              let dropped = false;
              const next: Record<string, PendingQueueEntry[]> = {};
              for (const n of names) {
                const current = listOf(s, n);
                const kept = current.filter((e) => e.queuedAt >= cutoff);
                if (kept.length !== current.length) dropped = true;
                next[n as string] = kept;
              }
              // No-op unless something was actually dropped — a fresh array on
              // every call would re-trigger the flusher effect in a hot loop.
              return dropped ? (next as unknown as Partial<State>) : {};
            }),
        } as State;
      },
      {
        name: opts.storageKey,
        storage: createJSONStorage(() => localStorage),
        version: 1,
      },
    ),
  );

  // Multi-tab: zustand persist is whole-store last-write-wins on one key — a
  // second tab hydrated earlier would clobber this tab's queued entries on its
  // next write. Best-effort merge: rehydrate from storage whenever another tab
  // writes the key, shrinking the race to the in-flight write itself.
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e) => {
      if (e.key === opts.storageKey) void useStore.persist.rehydrate();
    });
  }

  return useStore;
}
